"use client";

import React from "react";
import { cn } from "@/lib/utils";

interface AIIconProps {
  size?: "sm" | "md" | "lg";
  variant?: "default" | "animated" | "minimal" | "neural" | "learning";
  className?: string;
  isActive?: boolean;
}

export default function AIIcon({
  size = "md",
  variant = "default",
  className,
  isActive = false,
}: AIIconProps) {
  const sizeClasses = {
    sm: "w-4 h-4",
    md: "w-5 h-5",
    lg: "w-10 h-10",
  };

  // 默认AI图标 - 现代几何设计
  if (variant === "default") {
    return (
      <div
        className={cn(
          "relative inline-flex items-center justify-center",
          sizeClasses[size],
          className,
        )}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          className={cn(
            "transition-all duration-300 text-muted-foreground group-hover:text-primary dark:text-muted-foreground dark:group-hover:text-primary",
            isActive && "text-primary drop-shadow-sm dark:text-primary",
          )}
        >
          {/* 外圈 - 代表AI边界 */}
          <circle
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="1.5"
            fill="none"
            className="opacity-60"
          />

          {/* 内部神经网络结构 */}
          <g className="opacity-80">
            {/* 中心核心 */}
            <circle cx="12" cy="12" r="2" fill="currentColor" />

            {/* 连接节点 */}
            <circle
              cx="8"
              cy="8"
              r="1.5"
              fill="currentColor"
              className="opacity-70"
            />
            <circle
              cx="16"
              cy="8"
              r="1.5"
              fill="currentColor"
              className="opacity-70"
            />
            <circle
              cx="8"
              cy="16"
              r="1.5"
              fill="currentColor"
              className="opacity-70"
            />
            <circle
              cx="16"
              cy="16"
              r="1.5"
              fill="currentColor"
              className="opacity-70"
            />

            {/* 连接线 */}
            <line
              x1="10.5"
              y1="10.5"
              x2="8"
              y2="8"
              stroke="currentColor"
              strokeWidth="1"
              className="opacity-50"
            />
            <line
              x1="13.5"
              y1="10.5"
              x2="16"
              y2="8"
              stroke="currentColor"
              strokeWidth="1"
              className="opacity-50"
            />
            <line
              x1="10.5"
              y1="13.5"
              x2="8"
              y2="16"
              stroke="currentColor"
              strokeWidth="1"
              className="opacity-50"
            />
            <line
              x1="13.5"
              y1="13.5"
              x2="16"
              y2="16"
              stroke="currentColor"
              strokeWidth="1"
              className="opacity-50"
            />
          </g>

          {/* 智能光环效果 */}
          {isActive && (
            <circle
              cx="12"
              cy="12"
              r="11"
              stroke="currentColor"
              strokeWidth="0.5"
              fill="none"
              className="opacity-30 animate-pulse"
            />
          )}
        </svg>
      </div>
    );
  }

  // 动画AI图标 - 带有动态效果
  if (variant === "animated") {
    return (
      <div
        className={cn(
          "relative inline-flex items-center justify-center",
          sizeClasses[size],
          className,
        )}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          className={cn(
            "transition-all duration-300",
            isActive
              ? "text-primary drop-shadow-sm dark:text-primary"
              : "text-muted-foreground group-hover:text-primary dark:text-muted-foreground dark:group-hover:text-primary",
          )}
        >
          {/* 旋转外环 */}
          <circle
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="1"
            fill="none"
            strokeDasharray="8 4"
            className="opacity-40 animate-spin"
            style={{ animationDuration: "8s" }}
          />

          {/* 反向旋转内环 */}
          <circle
            cx="12"
            cy="12"
            r="7"
            stroke="currentColor"
            strokeWidth="1"
            fill="none"
            strokeDasharray="4 2"
            className="opacity-60 animate-spin"
            style={{ animationDuration: "6s", animationDirection: "reverse" }}
          />

          {/* 脉冲中心 */}
          <circle
            cx="12"
            cy="12"
            r="3"
            fill="currentColor"
            className="opacity-80 animate-pulse"
          />

          {/* 数据流点 */}
          <g className="animate-pulse">
            <circle
              cx="12"
              cy="5"
              r="1"
              fill="currentColor"
              className="opacity-60"
            />
            <circle
              cx="19"
              cy="12"
              r="1"
              fill="currentColor"
              className="opacity-60"
            />
            <circle
              cx="12"
              cy="19"
              r="1"
              fill="currentColor"
              className="opacity-60"
            />
            <circle
              cx="5"
              cy="12"
              r="1"
              fill="currentColor"
              className="opacity-60"
            />
          </g>
        </svg>
      </div>
    );
  }

  // 极简AI图标 - 简洁现代
  if (variant === "minimal") {
    return (
      <div
        className={cn(
          "relative inline-flex items-center justify-center",
          sizeClasses[size],
          className,
        )}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          className={cn(
            "transition-all duration-300",
            isActive
              ? "text-primary drop-shadow-sm dark:text-primary"
              : "text-muted-foreground group-hover:text-primary dark:text-muted-foreground dark:group-hover:text-primary",
          )}
        >
          {/* 简约AI大脑轮廓 */}
          <path
            d="M12 3C16.97 3 21 7.03 21 12C21 16.97 16.97 21 12 21C7.03 21 3 16.97 3 12C3 7.03 7.03 3 12 3Z"
            stroke="currentColor"
            strokeWidth="1.5"
            fill="none"
          />

          {/* 内部连接点 */}
          <circle cx="9" cy="9" r="1" fill="currentColor" />
          <circle cx="15" cy="9" r="1" fill="currentColor" />
          <circle cx="12" cy="12" r="1.5" fill="currentColor" />
          <circle cx="9" cy="15" r="1" fill="currentColor" />
          <circle cx="15" cy="15" r="1" fill="currentColor" />

          {/* 连接线 */}
          <path
            d="M9 9 L12 12 L15 9 M9 15 L12 12 L15 15"
            stroke="currentColor"
            strokeWidth="1"
            className="opacity-60"
          />
        </svg>
      </div>
    );
  }

  // 神经网络AI图标 - 复杂网络结构
  if (variant === "neural") {
    return (
      <div
        className={cn(
          "relative inline-flex items-center justify-center",
          sizeClasses[size],
          className,
        )}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          className={cn(
            "transition-all duration-300",
            isActive
              ? "text-primary drop-shadow-sm dark:text-primary"
              : "text-muted-foreground group-hover:text-primary dark:text-muted-foreground dark:group-hover:text-primary",
          )}
        >
          {/* 神经网络节点 */}
          <g className="opacity-80">
            {/* 输入层 */}
            <circle cx="6" cy="8" r="1.5" fill="currentColor" />
            <circle cx="6" cy="12" r="1.5" fill="currentColor" />
            <circle cx="6" cy="16" r="1.5" fill="currentColor" />

            {/* 隐藏层 */}
            <circle cx="12" cy="6" r="1.5" fill="currentColor" />
            <circle cx="12" cy="10" r="1.5" fill="currentColor" />
            <circle cx="12" cy="14" r="1.5" fill="currentColor" />
            <circle cx="12" cy="18" r="1.5" fill="currentColor" />

            {/* 输出层 */}
            <circle cx="18" cy="10" r="1.5" fill="currentColor" />
            <circle cx="18" cy="14" r="1.5" fill="currentColor" />
          </g>

          {/* 连接线网络 */}
          <g className="opacity-40" stroke="currentColor" strokeWidth="0.8">
            {/* 输入到隐藏层 */}
            <line x1="7.5" y1="8" x2="10.5" y2="6" />
            <line x1="7.5" y1="8" x2="10.5" y2="10" />
            <line x1="7.5" y1="12" x2="10.5" y2="10" />
            <line x1="7.5" y1="12" x2="10.5" y2="14" />
            <line x1="7.5" y1="16" x2="10.5" y2="14" />
            <line x1="7.5" y1="16" x2="10.5" y2="18" />

            {/* 隐藏层到输出层 */}
            <line x1="13.5" y1="6" x2="16.5" y2="10" />
            <line x1="13.5" y1="10" x2="16.5" y2="10" />
            <line x1="13.5" y1="10" x2="16.5" y2="14" />
            <line x1="13.5" y1="14" x2="16.5" y2="14" />
            <line x1="13.5" y1="18" x2="16.5" y2="14" />
          </g>

          {/* 活跃状态指示 */}
          {isActive && (
            <g className="animate-pulse">
              <circle
                cx="12"
                cy="12"
                r="8"
                stroke="currentColor"
                strokeWidth="0.5"
                fill="none"
                className="opacity-20"
              />
            </g>
          )}
        </svg>
      </div>
    );
  }

  // 学习助手AI图标 - 有机四芒星设计（Gemini/ChatGPT风格，全曲线无棱角）
  // 主体：纯贝塞尔曲线四芒星 + 玻璃高光 + 琥珀AI中心火花
  if (variant === "learning") {
    return (
      <div
        className={cn(
          "relative inline-flex items-center justify-center",
          sizeClasses[size],
          className,
        )}
      >
        <svg viewBox="0 0 32 32" fill="none" className="w-full h-full">
          <defs>
            {/* 主渐变：靛紫 → 紫罗兰 → 紫 */}
            <linearGradient
              id="ai-lrn-g1"
              x1="0"
              y1="0"
              x2="32"
              y2="32"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0%" stopColor="#5B21B6" />
              <stop offset="45%" stopColor="#7C3AED" />
              <stop offset="80%" stopColor="#A855F7" />
              <stop offset="100%" stopColor="#C026D3" />
            </linearGradient>
            {/* 玻璃高光渐变（上左 → 透明） */}
            <linearGradient
              id="ai-lrn-hi"
              x1="0"
              y1="0"
              x2="20"
              y2="20"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0%" stopColor="white" stopOpacity="0.38" />
              <stop offset="60%" stopColor="white" stopOpacity="0.08" />
              <stop offset="100%" stopColor="white" stopOpacity="0" />
            </linearGradient>
            {/* 中心火花渐变：明黄 → 琥珀 */}
            <linearGradient
              id="ai-lrn-sp"
              x1="12"
              y1="11"
              x2="20"
              y2="21"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0%" stopColor="#FDE68A" />
              <stop offset="100%" stopColor="#F59E0B" />
            </linearGradient>
            {/* 活跃光晕 */}
            <radialGradient id="ai-lrn-glow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#8B5CF6" stopOpacity="0.45" />
              <stop offset="70%" stopColor="#7C3AED" stopOpacity="0.15" />
              <stop offset="100%" stopColor="#7C3AED" stopOpacity="0" />
            </radialGradient>
          </defs>

          {/* 活跃光晕（脉冲） */}
          {isActive && (
            <circle
              cx="16"
              cy="16"
              r="15.5"
              fill="url(#ai-lrn-glow)"
              className="animate-pulse"
            />
          )}

          {/*
            主四芒星：全贝塞尔曲线，无任何直线/棱角
            算法：每段 C P1 P2 端点，P1/P2 都拉向中心(16,16)
            使四段曲线在两臂之间向内收，形成有机柔和的星形
          */}
          <path
            d="M16 1.5
               C 16 8.5,  23.5 16, 30.5 16
               C 23.5 16, 16 23.5, 16 30.5
               C 16 23.5, 8.5 16,  1.5 16
               C 8.5 16,  16 8.5,  16 1.5
               Z"
            fill="url(#ai-lrn-g1)"
            className={cn(
              "transition-all duration-300",
              isActive && "drop-shadow-lg",
            )}
          />

          {/* 玻璃质感高光层（覆盖在同一路径上） */}
          <path
            d="M16 1.5
               C 16 8.5,  23.5 16, 30.5 16
               C 23.5 16, 16 23.5, 16 30.5
               C 16 23.5, 8.5 16,  1.5 16
               C 8.5 16,  16 8.5,  16 1.5
               Z"
            fill="url(#ai-lrn-hi)"
          />

          {/*
            中心 AI 火花：同款有机四芒星缩小版（1/3尺寸）
            臂长 5 单位（vs 外星 14.5 单位）
          */}
          <path
            d="M16 11
               C 16 13.24, 18.76 16, 21 16
               C 18.76 16, 16 18.76, 16 21
               C 16 18.76, 13.24 16, 11 16
               C 13.24 16, 16 13.24, 16 11
               Z"
            fill="url(#ai-lrn-sp)"
            opacity={isActive ? 1 : 0.88}
            className={cn(
              "transition-all duration-300",
              isActive && "animate-pulse",
            )}
          />
        </svg>
      </div>
    );
  }

  return null;
}
